<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import FeatureLockedScreen from 'dashboard/components/FeatureLockedScreen.vue';
import { catalogItemsApi } from 'dashboard/api/catalogItems';

const { t } = useI18n();
const { accountId } = useAccount();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

const hasFeature = computed(() =>
  isFeatureEnabled.value(accountId.value, 'general_catalog')
);

const items = ref([]);
const loading = ref(false);
const search = ref('');
const showModal = ref(false);
const saving = ref(false);
const editingItem = ref(null);
const confirmDelete = ref(null);

const emptyForm = () => ({
  code: '',
  name: '',
  description: '',
  category: '',
  price: 0,
  commission_type: 'percentage',
  commission_value: 0,
  color: '#3b82f6',
  active: true,
  position: 0,
});

const form = ref(emptyForm());

const filtered = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return items.value;
  return items.value.filter(
    p =>
      p.name.toLowerCase().includes(q) ||
      p.code.toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
  );
});

async function fetchItems() {
  loading.value = true;
  try {
    const res = await catalogItemsApi.list();
    items.value = res.data?.payload || [];
  } catch {
    // silent
  } finally {
    loading.value = false;
  }
}

function openNew() {
  editingItem.value = null;
  form.value = emptyForm();
  showModal.value = true;
}

function openEdit(item) {
  editingItem.value = item;
  form.value = {
    code: item.code,
    name: item.name,
    description: item.description || '',
    category: item.category || '',
    price: item.price || 0,
    commission_type: item.commission_type || 'percentage',
    commission_value: item.commission_value || 0,
    color: item.color || '#3b82f6',
    active: item.active,
    position: item.position || 0,
  };
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  editingItem.value = null;
}

async function saveItem() {
  saving.value = true;
  try {
    if (editingItem.value) {
      const res = await catalogItemsApi.update(
        editingItem.value.id,
        form.value
      );
      const idx = items.value.findIndex(p => p.id === editingItem.value.id);
      if (idx !== -1) items.value[idx] = res.data;
    } else {
      const res = await catalogItemsApi.create(form.value);
      items.value.push(res.data);
    }
    closeModal();
  } catch {
    // silent
  } finally {
    saving.value = false;
  }
}

async function toggleActive(item) {
  try {
    const res = await catalogItemsApi.update(item.id, { active: !item.active });
    const idx = items.value.findIndex(p => p.id === item.id);
    if (idx !== -1) items.value[idx] = res.data;
  } catch {
    // silent
  }
}

function askDelete(item) {
  confirmDelete.value = item;
}

async function doDelete() {
  if (!confirmDelete.value) return;
  try {
    await catalogItemsApi.delete(confirmDelete.value.id);
    items.value = items.value.filter(p => p.id !== confirmDelete.value.id);
  } catch {
    // silent
  } finally {
    confirmDelete.value = null;
  }
}

function formatPrice(val) {
  return Number(val || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatCommission(item) {
  if (item.commission_type === 'fixed') {
    return formatPrice(item.commission_value);
  }
  return `${Number(item.commission_value || 0)}%`;
}

const commissionPlaceholder = computed(() => {
  return form.value.commission_type === 'fixed'
    ? t('CATALOG.FORM.COMMISSION_VALUE_PLACEHOLDER_FIXED')
    : t('CATALOG.FORM.COMMISSION_VALUE_PLACEHOLDER_PERCENTAGE');
});

onMounted(fetchItems);
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <FeatureLockedScreen v-if="!hasFeature" feature-name="general_catalog" />

    <template v-else>
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
      >
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('CATALOG.TITLE') }}
          </h1>
          <p class="mt-0.5 text-sm text-n-slate-11">
            {{ t('CATALOG.SUBTITLE') }}
          </p>
        </div>
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90 transition-colors"
          @click="openNew"
        >
          <i class="i-lucide-plus size-4" />
          {{ t('CATALOG.ADD_BUTTON') }}
        </button>
      </div>

      <!-- Search -->
      <div class="px-6 py-3 border-b border-n-weak">
        <div class="relative max-w-xs">
          <i
            class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-10"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('CATALOG.SEARCH_PLACEHOLDER')"
            class="w-full py-2 pr-3 text-sm border rounded-lg pl-9 border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Table -->
      <div class="flex-1 px-6 py-4 overflow-auto">
        <div v-if="loading" class="flex items-center justify-center h-40">
          <i class="i-lucide-loader-2 size-6 text-n-slate-10 animate-spin" />
        </div>

        <div
          v-else-if="filtered.length === 0"
          class="flex flex-col items-center justify-center h-40 text-n-slate-10"
        >
          <i class="mb-2 i-lucide-package size-10" />
          <span class="text-sm">{{ t('CATALOG.EMPTY_STATE') }}</span>
        </div>

        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left border-b border-n-weak text-n-slate-10">
              <th class="pb-3 pr-4 font-medium">
                {{ t('CATALOG.TABLE.CODE') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('CATALOG.TABLE.NAME') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('CATALOG.TABLE.CATEGORY') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('CATALOG.TABLE.PRICE') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('CATALOG.TABLE.COMMISSION') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('CATALOG.TABLE.COLOR') }}
              </th>
              <th class="pb-3 pr-4 font-medium">
                {{ t('CATALOG.TABLE.ACTIVE') }}
              </th>
              <th class="pb-3 font-medium text-right">
                {{ t('CATALOG.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filtered"
              :key="item.id"
              class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
            >
              <td class="py-3 pr-4">
                <span class="font-mono font-semibold text-n-slate-12">
                  {{ item.code }}
                </span>
              </td>
              <td class="py-3 pr-4 text-n-slate-12">
                {{ item.name }}
              </td>
              <td class="py-3 pr-4">
                <span
                  v-if="item.category"
                  class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ item.category }}
                </span>
                <span v-else class="text-xs text-n-slate-9">-</span>
              </td>
              <td class="py-3 pr-4 font-mono text-n-slate-12">
                {{ formatPrice(item.price) }}
              </td>
              <td class="py-3 pr-4">
                <span
                  class="px-2 py-0.5 text-xs rounded-full font-medium"
                  :class="
                    item.commission_type === 'fixed'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-emerald-100 text-emerald-800'
                  "
                >
                  {{ formatCommission(item) }}
                </span>
              </td>
              <td class="py-3 pr-4">
                <div class="flex items-center gap-2">
                  <div
                    class="rounded-full size-5 border border-n-weak/50"
                    :style="{ backgroundColor: item.color }"
                  />
                </div>
              </td>
              <td class="py-3 pr-4">
                <button
                  class="relative inline-flex items-center h-5 rounded-full w-9 transition-colors focus:outline-none"
                  :class="item.active ? 'bg-n-brand' : 'bg-n-slate-6'"
                  @click="toggleActive(item)"
                >
                  <span
                    class="inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform"
                    :class="item.active ? 'translate-x-4' : 'translate-x-0.5'"
                  />
                </button>
              </td>
              <td class="py-3 text-right">
                <div class="flex items-center justify-end gap-2">
                  <button
                    class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                    @click="openEdit(item)"
                  >
                    <i class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-ruby-9 transition-colors"
                    @click="askDelete(item)"
                  >
                    <i class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Create/Edit Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="closeModal"
      >
        <div
          class="w-full max-w-lg p-6 mx-4 rounded-xl shadow-xl bg-n-background"
        >
          <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
            {{
              editingItem
                ? t('CATALOG.FORM.TITLE_EDIT')
                : t('CATALOG.FORM.TITLE_NEW')
            }}
          </h2>

          <div class="space-y-4">
            <!-- Code + Name -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.CODE') }}
                </label>
                <input
                  v-model="form.code"
                  type="text"
                  :placeholder="t('CATALOG.FORM.CODE_PLACEHOLDER')"
                  class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.NAME') }}
                </label>
                <input
                  v-model="form.name"
                  type="text"
                  :placeholder="t('CATALOG.FORM.NAME_PLACEHOLDER')"
                  class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>

            <!-- Description -->
            <div>
              <label class="block mb-1 text-xs font-medium text-n-slate-11">
                {{ t('CATALOG.FORM.DESCRIPTION') }}
              </label>
              <textarea
                v-model="form.description"
                rows="2"
                :placeholder="t('CATALOG.FORM.DESCRIPTION_PLACEHOLDER')"
                class="w-full px-3 py-2 text-sm border rounded-lg resize-none border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>

            <!-- Category + Price -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.CATEGORY') }}
                </label>
                <input
                  v-model="form.category"
                  type="text"
                  :placeholder="t('CATALOG.FORM.CATEGORY_PLACEHOLDER')"
                  class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.PRICE') }}
                </label>
                <input
                  v-model.number="form.price"
                  type="number"
                  step="0.01"
                  min="0"
                  :placeholder="t('CATALOG.FORM.PRICE_PLACEHOLDER')"
                  class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>

            <!-- Commission Type + Value -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.COMMISSION_TYPE') }}
                </label>
                <select
                  v-model="form.commission_type"
                  class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                >
                  <option value="percentage">
                    {{ t('CATALOG.COMMISSION_TYPES.percentage') }}
                  </option>
                  <option value="fixed">
                    {{ t('CATALOG.COMMISSION_TYPES.fixed') }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.COMMISSION_VALUE') }}
                </label>
                <input
                  v-model.number="form.commission_value"
                  type="number"
                  step="0.01"
                  min="0"
                  :placeholder="commissionPlaceholder"
                  class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>

            <!-- Color + Active -->
            <div class="flex items-center gap-4">
              <div class="flex-1">
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.COLOR') }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    v-model="form.color"
                    type="color"
                    class="border rounded cursor-pointer h-9 w-12 border-n-weak"
                  />
                  <input
                    v-model="form.color"
                    type="text"
                    class="flex-1 px-3 py-2 font-mono text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  />
                </div>
              </div>
              <div>
                <label class="block mb-1 text-xs font-medium text-n-slate-11">
                  {{ t('CATALOG.FORM.ACTIVE') }}
                </label>
                <button
                  class="relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none"
                  :class="form.active ? 'bg-n-brand' : 'bg-n-slate-6'"
                  type="button"
                  @click="form.active = !form.active"
                >
                  <span
                    class="inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform"
                    :class="form.active ? 'translate-x-5' : 'translate-x-0.5'"
                  />
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="closeModal"
            >
              {{ t('CATALOG.FORM.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90 transition-colors disabled:opacity-50"
              :disabled="saving || !form.name || !form.code"
              @click="saveItem"
            >
              <i
                v-if="saving"
                class="mr-1 i-lucide-loader-2 size-4 animate-spin"
              />
              {{ t('CATALOG.FORM.SAVE') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirm Modal -->
      <div
        v-if="confirmDelete"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="confirmDelete = null"
      >
        <div
          class="w-full max-w-sm p-6 mx-4 rounded-xl shadow-xl bg-n-background"
        >
          <h2 class="mb-2 text-lg font-semibold text-n-slate-12">
            {{ t('CATALOG.CONFIRM_DELETE.TITLE') }}
          </h2>
          <p class="mb-6 text-sm text-n-slate-11">
            {{
              t('CATALOG.CONFIRM_DELETE.MESSAGE', {
                name: confirmDelete.name,
              })
            }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="confirmDelete = null"
            >
              {{ t('CATALOG.CONFIRM_DELETE.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-ruby-9 hover:bg-ruby-10 transition-colors"
              @click="doDelete"
            >
              {{ t('CATALOG.CONFIRM_DELETE.CONFIRM') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
